// Wirefilter condition expression parse/serialize — ported verbatim from the
// legacy SwiftFederation components/ConditionBuilder.tsx. Pure logic, shared by
// the Header Rules visual builder (and reusable elsewhere).

export interface ConditionRule {
  id: string;
  field: string;
  operator: string;
  value: string;
}
export interface ConditionGroup {
  id: string;
  rules: ConditionRule[];
}

let _cbId = 0;
export const cbId = () => `c${++_cbId}`;

export const FIELD_GROUPS = [
  { group: 'URI', items: [
    { value: 'http.uri.path', label: 'URI Path' },
    { value: 'http.uri.query', label: 'Query String' },
  ] },
  { group: 'Request', items: [
    { value: 'http.host', label: 'Hostname' },
    { value: 'http.method', label: 'HTTP Method' },
  ] },
  { group: 'Headers', items: [
    { value: 'http.user_agent', label: 'User-Agent' },
    { value: 'http.referer', label: 'Referer' },
    { value: 'http.content_type', label: 'Content-Type' },
  ] },
  { group: 'Network', items: [
    { value: 'ip.src', label: 'Client IP' },
    { value: 'http.geo.country', label: 'Country Code' },
    { value: 'http.geo.asn', label: 'ASN Number' },
  ] },
  { group: 'Connection', items: [
    { value: 'ssl', label: 'Is HTTPS' },
    { value: 'http.request_length', label: 'Request Body Size' },
  ] },
];

export const OPERATORS = [
  { value: '==', label: 'equals' },
  { value: '!=', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'matches', label: 'matches regex' },
  { value: 'not_matches', label: 'does not match regex' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'in', label: 'is in list' },
];

// Wirefilter is typed: IP and number fields reject substring/regex operators
// ("cannot use this operation type Ip") and exact-match enums only make sense
// with ==/!=/in. Restrict the operator menu per field type to avoid sending
// expressions the engine rejects at parse time.
const IP_FIELDS = new Set(['ip.src']);
const NUMERIC_FIELDS = new Set(['http.geo.asn', 'http.request_length']);
const EXACT_ONLY_FIELDS = new Set(['http.method', 'http.geo.country', ...IP_FIELDS, ...NUMERIC_FIELDS]);
const EXACT_OPS = new Set(['==', '!=', 'in']);

/** Operators valid for a given field (string fields get the full set). */
export function operatorsForField(field: string): typeof OPERATORS {
  return EXACT_ONLY_FIELDS.has(field) ? OPERATORS.filter((o) => EXACT_OPS.has(o.value)) : OPERATORS;
}

/** IP / numeric literals must NOT be quoted in wirefilter (e.g. ip.src == 1.2.3.4). */
const isUnquotedField = (field: string) => IP_FIELDS.has(field) || NUMERIC_FIELDS.has(field);

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
export const COUNTRY_CODES = ['US', 'GB', 'DE', 'FR', 'JP', 'CN', 'KR', 'VN', 'SG', 'AU', 'CA', 'BR', 'IN', 'RU'];

export function placeholderForField(field: string): string {
  switch (field) {
    case 'http.uri.path': return '/api/v1/users';
    case 'http.uri.query': return 'page=1&sort=name';
    case 'http.host': return 'example.com';
    case 'http.user_agent': return 'Mozilla/5.0...';
    case 'http.referer': return 'https://example.com';
    case 'http.content_type': return 'application/json';
    case 'ip.src': return '1.2.3.4 or 10.0.0.0/8';
    case 'http.geo.asn': return '13335';
    case 'http.request_length': return '1024';
    default: return '';
  }
}

export function parseWirefilter(str: string): ConditionGroup[] | null {
  if (!str || !str.trim()) return [];
  try {
    const groups: ConditionGroup[] = [];
    const orChunks = str.split(' || ');
    for (let chunk of orChunks) {
      chunk = chunk.trim();
      if (chunk.startsWith('(') && chunk.endsWith(')')) chunk = chunk.substring(1, chunk.length - 1).trim();
      const andChunks = chunk.split(' && ');
      const rules: ConditionRule[] = [];
      for (let andChunk of andChunks) {
        andChunk = andChunk.trim();
        let isNot = false;
        if (andChunk.toLowerCase().startsWith('not (') && andChunk.endsWith(')')) {
          isNot = true;
          andChunk = andChunk.substring(5, andChunk.length - 1).trim();
        }
        if (andChunk === 'ssl') { rules.push({ id: cbId(), field: 'ssl', operator: isNot ? 'is_false' : 'is_true', value: '' }); continue; }
        if (andChunk === 'not ssl' || andChunk === '!ssl') { rules.push({ id: cbId(), field: 'ssl', operator: 'is_false', value: '' }); continue; }
        const match = andChunk.match(/^([a-z0-9._]+)\s*(==|!=|eq|ne|contains|matches|in)\s*(.*)$/i);
        if (!match) return null;
        const field = match[1];
        let operator = match[2].toLowerCase();
        if (operator === 'eq') operator = '==';
        if (operator === 'ne') operator = '!=';
        let value = match[3].trim();
        if (isNot) {
          if (operator === 'contains') operator = 'not_contains';
          else if (operator === 'matches') operator = 'not_matches';
        } else if (operator === 'matches') {
          let innerVal = value;
          if (innerVal.startsWith('"') && innerVal.endsWith('"')) innerVal = innerVal.substring(1, innerVal.length - 1);
          if (innerVal.startsWith('^') && !innerVal.endsWith('$')) { operator = 'starts_with'; value = '"' + innerVal.substring(1) + '"'; }
          else if (innerVal.endsWith('$') && !innerVal.startsWith('^')) { operator = 'ends_with'; value = '"' + innerVal.substring(0, innerVal.length - 1) + '"'; }
        }
        if (value.startsWith('"') && value.endsWith('"')) value = value.substring(1, value.length - 1);
        else if (value.startsWith('{') && value.endsWith('}')) {
          value = value.substring(1, value.length - 1).trim();
          if (value.startsWith('"') && value.endsWith('"')) value = value.substring(1, value.length - 1);
        }
        rules.push({ id: cbId(), field, operator, value });
      }
      groups.push({ id: cbId(), rules });
    }
    return groups;
  } catch {
    return null;
  }
}

export function serializeWirefilter(groups: ConditionGroup[]): string {
  if (groups.length === 0) return '';
  const groupStrings = groups
    .filter((g) => g.rules.length > 0)
    .map((group) => {
      const ruleStrings = group.rules.map((rule) => {
        if (rule.field === 'ssl') return rule.operator === 'is_false' ? 'not ssl' : 'ssl';
        if (!rule.field || !rule.value) return '';
        const op = rule.operator;
        let val = rule.value;
        if (op === 'not_contains') { if (!val.startsWith('"')) val = `"${val}"`; return `not (${rule.field} contains ${val})`; }
        if (op === 'not_matches') { if (!val.startsWith('"')) val = `"${val}"`; return `not (${rule.field} matches ${val})`; }
        if (op === 'starts_with') { val = val.replace(/^"|"/g, ''); return `${rule.field} matches "^${val}"`; }
        if (op === 'ends_with') { val = val.replace(/^"|"/g, ''); return `${rule.field} matches "${val}$"`; }
        const unquoted = isUnquotedField(rule.field);
        if (op === 'in') {
          val = val.replace(/^"|"/g, '').replace(/^{|}$/g, '').trim();
          return unquoted ? `${rule.field} in { ${val} }` : `${rule.field} in { "${val}" }`;
        }
        if (unquoted) { val = val.replace(/^"|"/g, ''); return `${rule.field} ${op} ${val}`; }
        if (!val.startsWith('"') && !val.startsWith('{')) val = `"${val}"`;
        return `${rule.field} ${op} ${val}`;
      });
      const filtered = ruleStrings.filter(Boolean);
      if (filtered.length === 0) return '';
      const groupStr = filtered.join(' && ');
      return filtered.length > 1 ? `(${groupStr})` : groupStr;
    })
    .filter(Boolean);
  return groupStrings.join(' || ');
}
